import {
  DEFAULT_SURGE_PLAY_THRESHOLD,
  numbersTouchedByPlay,
  resolveNumberExposure,
  type NumberCapConfig,
  type PlayLike,
} from './numberCaps';

export type SurgeAlertLevel = 'approaching' | 'surge' | 'cap_high';

export interface NumberSurgeAlert {
  number: string;
  window: string;
  level: SurgeAlertLevel;
  playCount: number;
  exposure: number;
  cap: number;
  tag: string;
}

const CAP_HIGH_RATIO = 0.9;
const APPROACHING_RATIO = 0.75;

export function surgeWarnPlayCount(config: NumberCapConfig): number {
  const threshold = config.surgePlayThreshold ?? DEFAULT_SURGE_PLAY_THRESHOLD;
  return Math.max(1, Math.floor(threshold * APPROACHING_RATIO));
}

function isOpenPlayInWindow(play: PlayLike, window: string): boolean {
  if (play.status && play.status !== 'open') return false;
  if (play.window && play.window !== window) return false;
  return true;
}

/** Distinct numbers with open plays in the active window. */
export function getActiveNumbersInWindow(plays: PlayLike[], window: string): string[] {
  const numbers = new Set<string>();
  for (const play of plays) {
    if (!isOpenPlayInWindow(play, window)) continue;
    numbersTouchedByPlay(play).forEach((n) => numbers.add(n));
  }
  return [...numbers];
}

/** Alerts bankers should see for heavy inscription volume on a number. */
export function collectNumberSurgeAlerts(
  plays: PlayLike[],
  config: NumberCapConfig,
  window: string,
  dayKey: string,
): NumberSurgeAlert[] {
  if (!window) return [];

  const warnAt = surgeWarnPlayCount(config);
  const alerts: NumberSurgeAlert[] = [];

  for (const number of getActiveNumbersInWindow(plays, window)) {
    const stats = resolveNumberExposure(number, config, plays, window);

    if (stats.surgeActive) {
      alerts.push({
        number,
        window,
        level: 'surge',
        playCount: stats.playCount,
        exposure: stats.exposure,
        cap: stats.cap,
        tag: `${dayKey}|${window}|${number}|surge`,
      });
    } else if (stats.playCount >= warnAt) {
      alerts.push({
        number,
        window,
        level: 'approaching',
        playCount: stats.playCount,
        exposure: stats.exposure,
        cap: stats.cap,
        tag: `${dayKey}|${window}|${number}|approaching`,
      });
    }

    if (stats.cap > 0 && stats.exposure >= stats.cap * CAP_HIGH_RATIO) {
      alerts.push({
        number,
        window,
        level: 'cap_high',
        playCount: stats.playCount,
        exposure: stats.exposure,
        cap: stats.cap,
        tag: `${dayKey}|${window}|${number}|cap_high`,
      });
    }
  }

  return alerts;
}

export function buildSurgeAlertPayload(alert: NumberSurgeAlert, lang: string) {
  const es = lang === 'es';
  const num = alert.number;
  const fmt = (n: number) => new Intl.NumberFormat().format(n);

  switch (alert.level) {
    case 'surge':
      return {
        cat: 'limits' as const,
        sev: 'danger' as const,
        urgent: false,
        title: es ? `Saturación · № ${num}` : `Surge · № ${num}`,
        body: es
          ? `${alert.playCount} jugadas abiertas · $${fmt(alert.exposure)} de $${fmt(alert.cap)}.`
          : `${alert.playCount} open plays · $${fmt(alert.exposure)} of $${fmt(alert.cap)}.`,
      };
    case 'approaching':
      return {
        cat: 'limits' as const,
        sev: 'warn' as const,
        urgent: false,
        title: es ? `Muchas jugadas · № ${num}` : `Heavy volume · № ${num}`,
        body: es
          ? `${alert.playCount} jugadas abiertas en esta ventana.`
          : `${alert.playCount} open plays in this window.`,
      };
    case 'cap_high':
      return {
        cat: 'limits' as const,
        sev: 'warn' as const,
        urgent: false,
        title: es ? `Tope casi lleno · № ${num}` : `Cap nearly full · № ${num}`,
        body: es
          ? `$${fmt(alert.exposure)} de $${fmt(alert.cap)} en exposición.`
          : `$${fmt(alert.exposure)} of $${fmt(alert.cap)} exposure.`,
      };
    default:
      return {
        cat: 'limits' as const,
        sev: 'warn' as const,
        urgent: false,
        title: num,
        body: '',
      };
  }
}
